/**
 * What a finished run says about itself, in words rather than identifiers.
 *
 * A result used to be anonymous: a summary, a tick or a cross, and nothing
 * about who did the work, how long it took, or what ran it. The finished-run
 * view carries all three, and this module is where they are turned into
 * readable lines and per-worker figures. Every number here is counted off rows
 * the browser already holds; nothing is estimated.
 *
 * No I/O and no clock access - a caller that needs the moment passes `nowMs`,
 * so the same rows always read the same way.
 */
import type { FinishedRun, MissionTask, Worker } from './data';
import { dailyOutcomes, type DayOutcome } from './stats';

/** What a run reads as when it does not say how long it took. */
export const DURATION_UNKNOWN = 'not recorded';
/** What a run reads as when nothing said which worker held it. */
export const WORKER_UNKNOWN = 'worker not recorded';
/** What a run reads as when the computer did not report what ran it. */
export const TOOL_UNKNOWN = 'tool not recorded';
/**
 * What a finished run is headed with when the task it belongs to is no longer
 * among the ones Command holds. A run older than the tasks in view still says
 * something; it does not go blank.
 */
export const TASK_UNKNOWN = 'Task no longer in view';

const SECOND_MS = 1_000;
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * How long the run took. Null when the run carries no start, when either time
 * cannot be read, or when it reports as having finished before it began - two
 * clocks disagreeing is not a duration, and saying so beats printing a
 * negative one.
 */
export function runDurationMs(run: Pick<FinishedRun, 'started_at' | 'reported_at'>): number | null {
  if (run.started_at === null) return null;
  const started = Date.parse(run.started_at);
  const reported = Date.parse(run.reported_at);
  if (Number.isNaN(started) || Number.isNaN(reported)) return null;
  const elapsed = reported - started;
  return elapsed < 0 ? null : elapsed;
}

/** A length of time said the way a person would say it. */
export function durationPhrase(ms: number | null): string {
  if (ms === null) return DURATION_UNKNOWN;
  if (ms < SECOND_MS) return 'under a second';
  if (ms < 90 * SECOND_MS) {
    const seconds = Math.round(ms / SECOND_MS);
    return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
  }
  if (ms < 90 * MINUTE_MS) {
    const minutes = Math.round(ms / MINUTE_MS);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  if (ms < 36 * HOUR_MS) {
    const hours = Math.round(ms / HOUR_MS);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  const days = Math.round(ms / DAY_MS);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/** A registry key as the owner reads it: the key spaced out, never a blank. */
export function keyPhrase(key: string | null): string | null {
  if (key === null) return null;
  const trimmed = key.trim();
  if (trimmed === '') return null;
  return trimmed.replaceAll('_', ' ').replaceAll('-', ' ');
}

/**
 * The tool that actually ran the task, and the model it used when one was
 * reported. Never the tool the run was assigned: a run that reported nothing
 * says so rather than repeating back what was meant to happen.
 */
export function toolPhrase(run: Pick<FinishedRun, 'runtime_key' | 'model_key'>): string {
  const runtime = keyPhrase(run.runtime_key);
  if (runtime === null) return TOOL_UNKNOWN;
  const model = keyPhrase(run.model_key);
  return model === null ? runtime : `${runtime} · ${model}`;
}

/** Whether what ran is not what the run was assigned - a handover, shown as one. */
export function ranSomethingElse(run: Pick<FinishedRun, 'runtime_key' | 'assigned_runtime_key'>): boolean {
  const ran = keyPhrase(run.runtime_key);
  const assigned = keyPhrase(run.assigned_runtime_key);
  return ran !== null && assigned !== null && ran !== assigned;
}

/**
 * The tool the run was given, when that is not the tool that ran it, or null when there is
 * nothing to point out.
 *
 * A run that was assigned one tool and finished on another used to say only that a handover
 * happened. Which tool was passed over is the whole of what the owner needs: it is the
 * difference between a computer choosing sensibly and a computer quietly working around the
 * choice he made. Both names come off the run's own row, so every such run says it, whatever
 * the computer that ran it is running.
 */
export function handoverPhrase(run: Pick<FinishedRun, 'runtime_key' | 'assigned_runtime_key'>): string | null {
  if (!ranSomethingElse(run)) return null;
  return keyPhrase(run.assigned_runtime_key);
}

/**
 * Task titles by task id, for putting a name on a finished run. Blank titles
 * are left out: a heading of nothing is worse than saying the task is not in
 * view.
 */
export function taskTitles(tasks: readonly MissionTask[]): Map<string, string> {
  const titles = new Map<string, string>();
  for (const task of tasks) {
    const title = task.title.trim();
    if (title !== '') titles.set(task.id, title);
  }
  return titles;
}

/**
 * What the run was asked to do, as the owner wrote it. The run's own summary
 * says how it went; this says which task went that way, which a wall of
 * results otherwise leaves him to guess.
 */
export function runTaskTitle(run: Pick<FinishedRun, 'task_id'>, titles: ReadonlyMap<string, string>): string {
  return titles.get(run.task_id) ?? TASK_UNKNOWN;
}

/** The worker that held the run, by number and by name, as far as either is known. */
export function workerPhrase(run: Pick<FinishedRun, 'worker_id' | 'worker_number'>, workers: readonly Worker[]): string {
  const known = run.worker_id === null ? null : workers.find((w) => w.id === run.worker_id) ?? null;
  const number = run.worker_number ?? known?.number ?? null;
  const name = known?.name ?? null;
  if (number === null) return name === null ? WORKER_UNKNOWN : name;
  return name === null ? `Worker ${number}` : `Worker ${number} · ${name}`;
}

/** The middle value, or the mean of the middle two when there is an even number of them. */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle] ?? null;
  const lower = sorted[middle - 1];
  const upper = sorted[middle];
  if (lower === undefined || upper === undefined) return null;
  return (lower + upper) / 2;
}

/** The mean, to the nearest millisecond, or null when there is nothing to average. */
export function average(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  let total = 0;
  for (const value of values) total += value;
  return Math.round(total / values.length);
}

/** What one worker's finished runs add up to. */
export interface WorkerRunTotals {
  /** Null for the runs whose worker is no longer recorded. */
  readonly workerId: string | null;
  readonly workerNumber: number | null;
  readonly label: string;
  readonly runs: number;
  readonly successes: number;
  readonly failures: number;
  /** Successes out of runs, 0 to 1. Null when the worker has finished nothing. */
  readonly successRate: number | null;
  readonly medianMs: number | null;
  readonly averageMs: number | null;
}

/** The key a run is grouped under: its worker, or the one bucket for the unattributed. */
function bucketOf(run: Pick<FinishedRun, 'worker_id'>): string {
  return run.worker_id ?? '';
}

/**
 * Per-worker figures over the runs Command holds. Every known worker appears,
 * including one that has finished nothing - an idle worker is information too -
 * and runs whose worker is no longer recorded are gathered at the end rather
 * than dropped.
 */
export function workerTotals(runs: readonly FinishedRun[], workers: readonly Worker[]): WorkerRunTotals[] {
  const order: string[] = [];
  const seen = new Map<string, { workerId: string | null; workerNumber: number | null; label: string; successes: number; failures: number; durations: number[] }>();
  const add = (key: string, seed: { workerId: string | null; workerNumber: number | null; label: string }) => {
    const found = seen.get(key);
    if (found) return found;
    const fresh = { ...seed, successes: 0, failures: 0, durations: [] as number[] };
    seen.set(key, fresh);
    order.push(key);
    return fresh;
  };
  for (const worker of workers) {
    add(worker.id, { workerId: worker.id, workerNumber: worker.number, label: `Worker ${worker.number} · ${worker.name}` });
  }
  for (const run of runs) {
    const key = bucketOf(run);
    const bucket = key === ''
      ? add('', { workerId: null, workerNumber: null, label: WORKER_UNKNOWN })
      : add(key, { workerId: run.worker_id, workerNumber: run.worker_number, label: workerPhrase(run, workers) });
    if (run.success) bucket.successes += 1;
    else bucket.failures += 1;
    const elapsed = runDurationMs(run);
    if (elapsed !== null) bucket.durations.push(elapsed);
  }
  const rows: WorkerRunTotals[] = [];
  for (const key of order) {
    const bucket = seen.get(key);
    if (!bucket) continue;
    const total = bucket.successes + bucket.failures;
    rows.push({
      workerId: bucket.workerId,
      workerNumber: bucket.workerNumber,
      label: bucket.label,
      runs: total,
      successes: bucket.successes,
      failures: bucket.failures,
      successRate: total === 0 ? null : bucket.successes / total,
      medianMs: median(bucket.durations),
      averageMs: average(bucket.durations),
    });
  }
  return rows.sort((a, b) => {
    if (a.workerNumber === null && b.workerNumber === null) return a.label.localeCompare(b.label);
    if (a.workerNumber === null) return 1;
    if (b.workerNumber === null) return -1;
    return a.workerNumber - b.workerNumber;
  });
}

/** A success rate as the owner reads it, or a dash when the worker has finished nothing. */
export function successRatePhrase(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}

/** The runs one worker finished; pass null for the ones whose worker is not recorded. */
export function runsForWorker(runs: readonly FinishedRun[], workerId: string | null): FinishedRun[] {
  return runs.filter((run) => (run.worker_id ?? null) === workerId);
}

/** The workload chart, cut to one worker. Same shape and same days as the whole-desk one. */
export function workerWorkload(runs: readonly FinishedRun[], workerId: string | null, nowMs: number, days: number): DayOutcome[] {
  return dailyOutcomes(runsForWorker(runs, workerId), nowMs, days);
}
